/**
 * @file: TypingIndicator.tsx
 *
 * Mobile-only, renders nothing: it drives the stack-navigator header title
 * from the room's typing + member streams via `setOptions`. Mounted under
 * `ChatConversationProvider` by the conversation screen on mobile (desktop renders an
 * in-pane `WiredChatConversationHeader` instead). The navigator's `ChatHeaderTitle`
 * splits the `\n` into the title line + the typing subtitle.
 */

import { useConversationPeerPresence } from '@euler/app/flows/chat/lib/conversation/hooks/useConversationPeerPresence';
import { useTypingIndicator } from '@euler/app/flows/chat/lib/conversation/hooks/useTypingIndicator';
import { composeConversationTitle } from '@euler/app/flows/chat/utils/conversationTitle';
import { useBehaviorSubject, useLocalObservable } from '@euler/utils/hooks';
import { useNavigation } from '@react-navigation/native';
import { FC, memo, useEffect } from 'react';
import { useChatConversation } from '../../lib/conversation/context';
import { ChatHeaderTitle } from './ChatHeaderTitle';

export const TypingIndicator: FC = memo(() => {
  const navigation = useNavigation();
  const { conversationHandle, members$ } = useChatConversation();
  const [members] = useBehaviorSubject(members$);
  const typing$ = useTypingIndicator();
  const typingNames = useLocalObservable(() => typing$, [], [typing$]);
  const { title, subtitle } = composeConversationTitle(
    conversationHandle,
    members.length,
    typingNames,
  );
  // Private chat: show 在线/离线 under the name; a typing indicator wins.
  const peerPresence = useConversationPeerPresence();
  const presenceText =
    peerPresence === 'online'
      ? '在线'
      : peerPresence === 'offline'
      ? '离线'
      : '';
  const finalSubtitle = subtitle || presenceText;
  const online = peerPresence === 'online';

  useEffect(() => {
    const text = finalSubtitle ? `${title}\n${finalSubtitle}` : title;
    navigation.setOptions({
      // `title` keeps the iOS back-button label; `headerTitle` renders the
      // name (+ green dot when online) above the 在线/离线 subtitle.
      title: text,
      headerTitle: () => (
        <ChatHeaderTitle online={online}>{text}</ChatHeaderTitle>
      ),
    });
  }, [navigation, title, finalSubtitle, online]);

  return null;
});
